import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  forwardContract,
  getApiCall,
  Moxoseparationinlisting,
  postApiCall,
  request66W,
} from 'api/apiService';
import { BUYER_SOLICITOR, GETBUYER_SOLICITOR_BY_ID } from 'constant/app.config';
import { useTheme } from '@emotion/react';
import { AddUser } from '../Marketplaceicons';
import { poppins500, poppins600 } from 'theme/common.style';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Settings,
} from '@mui/icons-material';
import Loading from 'assets/images/loading.png';
import Swal from 'sweetalert2';
import { SolicitorSelection } from './SolicitorSelectionBox';
import { BasicModal } from 'component/SolicitorModal/SolicitorModal';
import { Pendingwarning } from '../MakeOffers';
import { datacontext } from 'modules/AppLayout/AppLayout';

const SolicitorPopupHandler = ({
  open,
  onClose,
  actionType, // "forwardContract" | "request66w"
  propertyId,
  checkListArray = [],
  propertyAddress,
  groupCall,
}) => {
  const [loading, setLoading] = useState(false);
  const [solicitorList, setSolicitorList] = useState([]);
  const [selectedSolicitor, setSelectedSolicitor] = useState(null);
  const [dialogMode, setDialogMode] = useState(null);
  const [opendetails, setOpendetails] = useState(false);
  const [opensolicitorselection, setOpensolicitorselection] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [openAddSolicitor, setOpenAddSolicitor] = useState(false);
  const [atInitial, setAtInitial] = useState(false);
  const [isChangingSolicitor, setIsChangingSolicitor] = useState(false);
  const [openSendConfirm, setOpenSendConfirm] = useState(false);
  const [updatefunc, setUpdatefunc] = useState(false);
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const { realTimeLog } = useContext(datacontext);
  const normalizedPropertyId = Number(propertyId);
  const hasOpenedOnce = useRef(false);

  // UTILITY TO CHECK NON-EMPTY ARRAYS FOR RCP SEPARATION (handles nested empty arrays too)
  const isNotEmptyArray = (arr) =>
    Array.isArray(arr) &&
    arr.length > 0 &&
    !arr.every((item) => Array.isArray(item) && item.length === 0);

  //  UTILITY FUNCTION TO CHECK NON-EMPTY ARRAYS FOR RCP SEPARATION (handles nested empty arrays too)
  const moxoapis = async (res) => {
    if (isNotEmptyArray(res?.dataForBSBContractBinder))
      await Moxoseparationinlisting(
        'dataForBSBContractBinder',
        res?.dataForBSBContractBinder
      ); //1
    if (isNotEmptyArray(res?.dataForSaveConBSB))
      Moxoseparationinlisting('dataForSaveConBSB', res?.dataForSaveConBSB); //2
    if (isNotEmptyArray(res?.dataForContractApprove))
      Moxoseparationinlisting(
        'dataForContractApproval',
        res?.dataForContractApprove
      ); //3
    if (isNotEmptyArray(res?.dataForBinderApprove))
      Moxoseparationinlisting(
        'dataForBinderApproval',
        res?.dataForBinderApprove
      ); //4
  };

  // UTILITY TO GET FULL NAME
  const getName = (s) =>
    `${s?.solicitorName || ''} ${s?.solicitorSurName || ''}`.trim();

  // CONFIG BASED ON ACTION TYPE
  const config = useMemo(() => {
    if (actionType === 'request66w') {
      return {
        title: 'Request s66W',
        statusTitle: 's66W Documentation Status',
        confirmChangeTitle: 'Confirm s66W Request',
        confirmChangeText: (
          <>
            A s66W certificate request has already been sent to an alternative
            solicitor. Are you sure you want to send this request to{' '}
            <b>{getName(selectedSolicitor)}</b>?
          </>
        ),
        isDoneWarning:
          's66W certificate already requested from this solicitor. Please select a different solicitor to request again.',
        taskCode: '66W001',
        api: request66W,
      };
    }
    return {
      title: 'Request Contract Review',
      statusTitle: 'Contract Documentation Status',
      confirmChangeTitle: 'Confirm Contract Review',
      confirmChangeText: (
        <>
          A contract has already been sent to an alternative solicitor. Are you
          sure you want to send this contract to{' '}
          <b>{getName(selectedSolicitor)}</b>?
        </>
      ),
      isDoneWarning:
        'Contract already forwarded to this solicitor. Please select a different solicitor to send again.',
      taskCode: 'RevCon001',
      api: forwardContract,
    };
  }, [actionType, selectedSolicitor]);

  //  FUNCTION TO GET UNIQUE SOLICITORS BASED ON SOLICITOR ID, WITH PRIORITY TO CHECKLIST MATCH + SAME PROPERTY
  const getUniqueSolicitors = (list, checklistArray, propertyId) => {
    const map = new Map();

    list.forEach((item) => {
      const id = item.solicitorId;

      const existing = map.get(id);

      const isChecklistMatch = checklistArray?.some(
        (t) => Number(t.updatedBySolicitor) === Number(id)
      );

      const isSameProperty = Number(item.propertyId) === Number(propertyId);

      if (!existing) {
        map.set(id, item);
        return;
      }

      const existingChecklistMatch = checklistArray?.some(
        (t) => Number(t.updatedBySolicitor) === Number(existing.solicitorId)
      );

      const existingSameProperty =
        Number(existing.propertyId) === Number(propertyId);

      // 🔥 PRIORITY LOGIC

      // 1. checklist + property match
      if (isChecklistMatch && isSameProperty) {
        map.set(id, item);
        return;
      }

      // 2. property match
      if (!existingSameProperty && isSameProperty) {
        map.set(id, item);
        return;
      }

      // else keep existing
    });

    return Array.from(map.values());
  };

  // MEMOIZED CHECKLIST TASK MATCH FOR CURRENT ACTION
  const matchedTask = useMemo(() => {
    return checkListArray.find(
      (t) =>
        String(t?.taskCode || '').toLowerCase() ===
        String(config.taskCode).toLowerCase()
    );
  }, [checkListArray, config.taskCode]);

  // MEMOIZED SOLICITOR MATCH BASED ON CHECKLIST TASK (to determine if we already have a solicitor linked to the task)
  const requestedSolicitor = useMemo(() => {
    return solicitorList.find(
      (s) => Number(s.solicitorId) === Number(matchedTask?.updatedBySolicitor)
    );
  }, [solicitorList, matchedTask]);

  // SHOULD WE SHOW CHANGE SOLICITOR OPTION? (only if checklist task not done)
  const shouldShowChangeSolicitor = matchedTask?.isDone !== 1;

  // IS CURRENT STATUS "ACTION REQUIRED"? (if not, it's just status view with no actions)
  const isActionRequired =
    String(matchedTask?.taskStatus || '').toLowerCase() == 'action required';

  const isStatusView = !isActionRequired;

  // CHECK IF SELECTED SOLICITOR IS SAME AS THE ONE IN CHECKLIST (to prevent same solicitor selection when changing)
  const isSameSolicitor =
    Number(matchedTask?.updatedBySolicitor) ===
    Number(selectedSolicitor?.solicitorId);

  // UTILITY TO CHECK IF SOLICITOR IS APPROVED (linked status)
  const isApproved = (s) =>
    String(s?.isLinkedStatus || '').toLowerCase() === 'approved';

  // BUILD PAYLOAD FOR ONBOARDING SOLICITOR
  const buildPayload = (s) => ({
    propertyId: normalizedPropertyId,
    solicitorId: s?.solicitorId,
    solicitorName: s?.solicitorName,
    solicitorSurName: s?.solicitorSurName,
    solicitorEmail: s?.solicitorEmail,
    solicitorMobile: s?.solicitorMobile,
    solicitorAddress: s?.solicitorAddress,
    buyerId: s?.buyerId,
    isBuyerSolicitor: 1,
  });

  // BUILD PAYLOAD FOR FINAL ACTION API
  const actionInput = (s) => ({
    propertyId: propertyId,
    solicitorId: s?.solicitorId,
    ...(actionType === 'request66w' && { isBuyer: 1 }),
  });

  //  INITIAL FLOW
  useEffect(() => {
    if (!open) return;
    const init = async () => {
      try {
        // ✅ ONLY first time
        if (!hasOpenedOnce.current) {
          setLoading(true);
          setDialogMode('loading');
        }
        const res = await getApiCall(`${GETBUYER_SOLICITOR_BY_ID}`);

        if (res?.data?.status == 1) {
          localStorage.setItem('buyerSolicitor', 'Yes');
          localStorage.setItem(
            'buyerSolicitorDetails',
            JSON.stringify(res?.data?.data)
          );
        } else {
          localStorage.setItem('buyerSolicitor', 'No');
        }
        const list = Array.isArray(res) ? res : res?.data?.data || [];

        if (!list.length > 0) {
          setOpenAddSolicitor(true);
          setAtInitial(true);
          return;
        }

        const filteredList = getUniqueSolicitors(
          list,
          checkListArray,
          normalizedPropertyId
        );

        setSolicitorList(filteredList);

        // ✅ Step 1: filter only current property
        const propertySolicitors = list.filter(
          (s) => Number(s.propertyId) === Number(propertyId)
        );

        // 🔴 Case 1: No solicitor for property
        if (propertySolicitors.length === 0) {
          setDialogMode('selectSolicitor');
          return;
        }

        // ✅ Step 2: find task match (66W / Contract independent)
        const matchedTask = checkListArray.find(
          (t) =>
            String(t?.taskCode || '').toLowerCase() ===
            String(config.taskCode).toLowerCase()
        );

        // ✅ Case A: match with checklist
        const matchedSolicitor = propertySolicitors.find(
          (s) =>
            Number(s.solicitorId) === Number(matchedTask?.updatedBySolicitor)
        );

        // ✅ Case B: fallback → latest (last item)
        const latestSolicitor = propertySolicitors.at(-1);

        // ✅ Final selection
        const finalSolicitor = matchedSolicitor || latestSolicitor;

        setSelectedSolicitor(finalSolicitor);
        setDialogMode('confirmAction');
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
        hasOpenedOnce.current = true; //  mark as opened
      }
    };

    init();
  }, [open, updatefunc, realTimeLog]);

  // HANDLE CLOSE - reset all states
  const handleClose = () => {
    setDialogMode(null);
    setSelectedSolicitor(null);
    setIsChangingSolicitor(false);
    setOpensolicitorselection(false);
    setAtInitial(false);
    setSolicitorList([]);
    onClose();
  };

  // FINAL CONFIRM
  const handleConfirm = async () => {
    try {
      setLoading(true);
      // onboard if needed
      if (propertyId != selectedSolicitor?.propertyId) {
        const res = await postApiCall(
          BUYER_SOLICITOR,
          buildPayload(selectedSolicitor)
        );
        if (isNotEmptyArray(res?.data?.bsbPropertyBinder))
          await Moxoseparationinlisting(
            'dataForSolBinder',
            res?.data?.bsbPropertyBinder
          ); //1
        if (isNotEmptyArray(res?.data?.vsvPropertyBinder))
          await Moxoseparationinlisting(
            'dataForSolBinder',
            res?.data?.vsvPropertyBinder
          ); //2

        if (res?.data?.status == 1) {
          // ✅ update local storage
          const resp = await getApiCall(GETBUYER_SOLICITOR_BY_ID);

          if (resp?.data?.status == 1) {
            localStorage.setItem('buyerSolicitor', 'Yes');
            localStorage.setItem(
              'buyerSolicitorDetails',
              JSON.stringify(resp?.data?.data)
            );
          }
        } else {
          // ❌ error case
          Swal.fire({
            title: res?.data?.heading || 'Error',
            text: res?.data?.message || 'Something went wrong',
            icon: 'error',
            confirmButtonText: 'Close',
          });
        }
      }
      // final API
      await config.api(actionInput(selectedSolicitor)).then((resp) => {
        groupCall();
        moxoapis(resp);
        setIsChangingSolicitor(false);
        setOpensolicitorselection(false);
        if (resp?.status == 1) {
          let formattedMessage = resp?.message || '';

          if (actionType === 'request66w') {
            formattedMessage = formattedMessage.replace(
              /A s66W certificate for (.*?) has been requested from (.*?)\.?$/,
              (_, address, solicitor) =>
                `A s66W certificate for <strong>${address}</strong> has been requested from <strong>${solicitor}</strong>.`
            );
          } else {
            formattedMessage = formattedMessage.replace(
              /to (.*?) at/,
              (_, name) => `to <strong>${name}</strong> at`
            );
          }

          Swal.fire({
            title: resp?.heading || 'Success',
            html: formattedMessage,
            icon: 'success',
            confirmButtonText: 'Close',
          });
        } else {
          Swal.fire({
            title: resp?.heading || 'Error',
            text: resp?.message || 'Something went wrong',
            icon: 'error',
            confirmButtonText: 'Close',
          });
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // UTILITY TO GET LINK STATUS WARNINGS
  const getLinkWarning = (s) => {
    const status = String(s?.isLinkedStatus || '').toLowerCase();

    if (status === 'pending') {
      return `${getName(s)} has not yet accepted your link request.`;
    }

    if (status === 'revoked') {
      return `${getName(s)} has revoked your link request.`;
    }

    if (status === 'declined') {
      return `${getName(s)} has declined your link request.`;
    }

    return '';
  };

  // CONFIRMATION TEXT BASED ON ACTION TYPE + STATUS
  const confirmText = selectedSolicitor ? (
    actionType === 'request66w' ? (
      <>
        Please confirm you authorise us to request a s66W certificate for{' '}
        <b
          style={{
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#666666',
          }}
        >
          {propertyAddress}
        </b>
        &nbsp;from your legal representative&nbsp;
        <b
          style={{
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#666666',
          }}
        >
          {getName(selectedSolicitor)}
        </b>
      </>
    ) : (
      <>
        You are authorising the contract of sale to be forwarded to your legal
        representative&nbsp;
        <b
          style={{
            fontFamily: 'Poppins',
            fontWeight: 600,
            color: '#666666',
          }}
        >
          {getName(selectedSolicitor)}
        </b>{' '}
        for their review and approval.
      </>
    )
  ) : null;

  // STATUS TEXT BASED ON ACTION TYPE + STATUS
  const statusText = requestedSolicitor ? (
    actionType === 'request66w' ? (
      <>
        You've already requested the s66W certificate from your legal
        representative <b>{getName(requestedSolicitor)}</b>, but the process
        isn't completed yet.
      </>
    ) : (
      <>
        You've already forwarded the contract document to the legal
        representative <b>{getName(requestedSolicitor)}</b>, but the process
        isn't completed yet.
      </>
    )
  ) : null;

  // DETAILS TEXT BASED ON ACTION TYPE
  const detailsText =
    actionType === 'request66w'
      ? `Prior to requesting s66W signing you must on-board your preferred legal representative. Once on-boarded you will then be able to request s66W signing.`
      : `Prior to forwarding a contract of sale you must on-board your preferred legal representative. Once on-boarded you will then be able to request a contract review.`;

  // HANDLE ONBOARD SOLICITOR (called inside final confirm if solicitor is different)
  const handleOnboardSolicitor = async () => {
    try {
      setOnboardingLoading(true);

      const res = await postApiCall(
        BUYER_SOLICITOR,
        buildPayload(selectedSolicitor)
      );
      if (isNotEmptyArray(res?.data?.bsbPropertyBinder))
        await Moxoseparationinlisting(
          'dataForSolBinder',
          res?.data?.bsbPropertyBinder
        ); //1
      if (isNotEmptyArray(res?.data?.vsvPropertyBinder))
        await Moxoseparationinlisting(
          'dataForSolBinder',
          res?.data?.vsvPropertyBinder
        ); //2

      if (res?.data?.status == 1) {
        // ✅ update local storage
        const resp = await getApiCall(GETBUYER_SOLICITOR_BY_ID);

        if (resp?.data?.status == 1) {
          const latestList = resp?.data?.data || [];

          localStorage.setItem('buyerSolicitor', 'Yes');
          localStorage.setItem(
            'buyerSolicitorDetails',
            JSON.stringify(latestList)
          );

          // ✅ find latest solicitor for current property
          const samePropertyList = latestList.filter(
            (s) => Number(s.propertyId) === Number(propertyId)
          );

          const latestSolicitor =
            samePropertyList.length > 0
              ? samePropertyList[samePropertyList.length - 1]
              : null;

          // ✅ update state
          if (latestSolicitor) {
            setSelectedSolicitor(latestSolicitor);
          }
        }

        setOnboardingLoading(false);

        // ✅ SHOW SWAL POPUP
        await Swal.fire({
          title: res?.data?.heading || 'Success',
          text: res?.data?.message || 'Solicitor onboarded successfully',
          icon: 'success',
          confirmButtonText: 'Close',
        });

        // ✅ AFTER user clicks OK
        setDialogMode('confirmAction');
      } else {
        // ❌ error case
        setOnboardingLoading(false);

        Swal.fire({
          title: res?.data?.heading || 'Error',
          text: res?.data?.message || 'Something went wrong',
          icon: 'error',
          confirmButtonText: 'Close',
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setOnboardingLoading(false);
    }
  };

  return (
    <>
      {/* LOADING */}
      <Dialog open={open && dialogMode === 'loading'}>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="center">
            <img
              alt="Logo"
              src={Loading}
              sx="loaderImage"
              style={{ height: '50px', width: '50px' }}
            />{' '}
            <Typography sx={{ fontFamily: 'poppins' }}>
              Loading Details...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SELECT SOLICITOR */}
      <Dialog open={open && dialogMode === 'selectSolicitor'} fullWidth>
        <DialogTitle>
          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Typography sx={styles.propertyAddress}>
              {propertyAddress}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            display={'flex'}
            alignItems={'center'}
            padding={'10px 0px'}
          >
            <AddUser />
            <Typography sx={styles.titletextwithusericon}>
              Onboarding Legal Representative
            </Typography>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <SolicitorSelection
            solicitorList={solicitorList}
            selectedSolicitor={selectedSolicitor}
            setSelectedSolicitor={setSelectedSolicitor}
            getName={getName}
            isApproved={isApproved}
            setOpenAddSolicitor={setOpenAddSolicitor}
          />

          <Grid
            container
            sx={styles.detailsgrid}
            onClick={() => {
              setOpendetails(!opendetails);
            }}
          >
            <Typography
              color="primary"
              sx={{
                fontFamily: 'poppins',
                fontSize: matchessm ? '12px' : '16px',
              }}
            >
              Details&nbsp;
            </Typography>
            {opendetails ? (
              <KeyboardArrowUp color="primary" />
            ) : (
              <KeyboardArrowDown color="primary" />
            )}
          </Grid>
          {opendetails && (
            <Typography sx={styles.detailstext}>{detailsText}</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            sx={styles.cancelbutton}
            onClick={handleClose}
            disabled={onboardingLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            backgroundColor="#4CA4D6"
            disabled={!selectedSolicitor || !isApproved(selectedSolicitor)}
            onClick={() => {
              if (!onboardingLoading) {
                handleOnboardSolicitor();
              }
            }}
            sx={styles.actionbutton}
          >
            Confirm
          </Button>
          {onboardingLoading && (
            <img
              alt="Loader"
              src={Loading}
              className="loaderImage"
              style={{ height: '40px', width: '40px' }} // 🔹 smaller & aligned
            />
          )}
        </DialogActions>
      </Dialog>

      {/* FINAL CONFIRM */}
      <Dialog open={open && dialogMode === 'confirmAction'}>
        <DialogTitle sx={{ paddingBottom: 0 }}>
          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Typography sx={styles.propertyAddress}>
              {propertyAddress}
            </Typography>
          </Grid>
          <Grid
            item
            display={'flex'}
            alignItems={'center'}
            padding={'10px 0px'}
          >
            <Pendingwarning />
            <Typography sx={styles.titletext}>
              {isStatusView ? config.statusTitle : config.title}
            </Typography>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <Typography sx={styles.desctext}>
            {isStatusView ? statusText : confirmText}
          </Typography>
          {selectedSolicitor && !isApproved(selectedSolicitor) && (
            <Typography sx={styles.warningtext}>
              {getLinkWarning(selectedSolicitor)}
            </Typography>
          )}

          {isChangingSolicitor && isSameSolicitor && (
            <Typography sx={styles.warningtext}>
              {config.isDoneWarning}
            </Typography>
          )}

          <Grid item xs={12}>
            {shouldShowChangeSolicitor &&
              (opensolicitorselection ? (
                <Grid sx={{ margin: '10px 5px 0px -5px' }}>
                  <SolicitorSelection
                    solicitorList={solicitorList}
                    selectedSolicitor={selectedSolicitor}
                    setSelectedSolicitor={setSelectedSolicitor}
                    getName={getName}
                    isApproved={isApproved}
                    matchessm={matchessm}
                    setOpenAddSolicitor={setOpenAddSolicitor}
                  />
                </Grid>
              ) : (
                <Button
                  color="primary"
                  onClick={() => {
                    setIsChangingSolicitor(true);
                    setOpensolicitorselection(true);
                  }}
                  sx={styles.changesolicitorbutton}
                >
                  <Settings fontSize="small" />
                  &nbsp; Change Solicitor
                </Button>
              ))}
          </Grid>
          <Grid
            container
            sx={styles.detailsgrid}
            onClick={() => {
              setOpendetails(!opendetails);
            }}
          >
            <Typography
              color="primary"
              sx={{
                fontFamily: 'poppins',
                fontSize: matchessm ? '12px' : '16px',
              }}
            >
              Details&nbsp;
            </Typography>
            {opendetails ? (
              <KeyboardArrowUp color="primary" />
            ) : (
              <KeyboardArrowDown color="primary" />
            )}
          </Grid>
          {opendetails && (
            <Typography sx={styles.detailstext}>{detailsText}</Typography>
          )}
        </DialogContent>

        <DialogActions>
          {/* 🔴 STATUS VIEW (no change clicked) */}
          {isStatusView && !isChangingSolicitor ? (
            <Button
              variant="contained"
              onClick={handleClose}
              sx={styles.actionbutton}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                sx={styles.cancelbutton}
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                sx={styles.actionbutton}
                onClick={() => {
                  if (!loading) {
                    if (isChangingSolicitor && isStatusView) {
                      setOpenSendConfirm(true);
                    } else {
                      handleConfirm();
                    }
                  }
                }}
                disabled={
                  !selectedSolicitor ||
                  Number(selectedSolicitor?.isLinked) === 0 ||
                  (isChangingSolicitor && isSameSolicitor) // 🔥 prevent same solicitor
                }
              >
                Confirm
              </Button>
              {loading && (
                <img
                  alt="Loader"
                  src={Loading}
                  className="loaderImage"
                  style={{ height: '40px', width: '40px' }} // 🔹 smaller & aligned
                />
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* CONFIRMATION WHEN CHANGING SOLICITOR ON STATUS VIEW */}
      <Dialog open={openSendConfirm}>
        <DialogTitle sx={{ fontFamily: 'Poppins', paddingBottom: 0 }}>
          <Grid item xs={12} display={'flex'} alignItems={'center'}>
            <Typography sx={styles.propertyAddress}>
              {propertyAddress}
            </Typography>
          </Grid>
          <Grid
            item
            display={'flex'}
            alignItems={'center'}
            padding={'10px 0px'}
          >
            <Pendingwarning />
            <Typography sx={styles.titletext}>
              {config.confirmChangeTitle}
            </Typography>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <Typography sx={styles.desctext}>
            {config.confirmChangeText}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            sx={styles.cancelbutton}
            onClick={() => setOpenSendConfirm(false)}
          >
            Cancel
          </Button>

          <Button
            sx={styles.actionbutton}
            onClick={() => {
              setOpenSendConfirm(false);
              handleConfirm();
            }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD SOLICITOR MODAL (used inside solicitor selection) */}
      <BasicModal
        open={openAddSolicitor}
        setOpen={setOpenAddSolicitor}
        propertyId={propertyId}
        opensolicitor={true}
        setOpensolicitor={setOpenAddSolicitor}
        updatefunc={async () => {
          // REFRESH SOLICITOR LIST AFTER ADD
          setUpdatefunc(!updatefunc);
        }}
        atInitial={atInitial}
        handleCloseparent={handleClose}
      />
    </>
  );
};

// STYLES
const styles = {
  actionbutton: {
    padding: '20px 30px',
    height: '45px',
    width: '100px',
    fontSize: '16px',
    color: '#FFFFFF !important',
    fontWeight: '400',
    fontFamily: 'Poppins',
    backgroundColor: '#4CA4D6 !important',
    borderRadius: '5px',
    textTransform: 'capitalize',

    // 🔥 override MUI contained styles
    '&.MuiButton-contained': {
      backgroundColor: '#4CA4D6 !important',
    },

    '&.MuiButton-contained:hover': {
      backgroundColor: '#4CA4D6 !important',
    },

    '&.MuiButton-contained:active': {
      backgroundColor: '#4CA4D6 !important',
    },

    '&.MuiButton-contained:focus': {
      backgroundColor: '#4CA4D6 !important',
    },
    '&.Mui-disabled': {
      backgroundColor: '#c7ccce !important', // your disabled color
      color: '#666666 !important',
      opacity: 0.7, // optional
    },
  },
  cancelbutton: {
    padding: '20px 30px',
    height: '45px',
    width: '100px',
    fontSize: '14px',
    color: '#666666 !important',
    fontWeight: '400',
    fontFamily: 'Poppins',
    border: '1px solid rgba(17, 35, 71, 0.1) !important',
    borderRadius: '5px',
    textTransform: 'capitalize',
  },
  propertyAddress: {
    ...poppins500,
    fontSize: { xs: '10px', sm: '14px' },
    color: '#666',
    fontWeight: 400,
  },
  titletext: {
    ...poppins500,
    fontSize: { xs: '16px', sm: '24px' },
    paddingLeft: '12px',
    color: '#000000',
  },
  titletextwithusericon: {
    ...poppins500,
    fontSize: { xs: '16px', sm: '24px' },
    color: '#000000',
  },
  desctext: {
    paddingTop: '8px',
    fontFamily: 'poppins!important',
    fontSize: { xs: '12px', sm: '16px' },
    fontWeight: 400,
    color: '#666666',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '0px',
    justifyItems: 'self-end',
  },
  warningtext: {
    ...poppins500,
    color: '#d32f2f',
    fontSize: '16px',
    marginTop: '8px',
    display: 'block',
  },
  changesolicitorbutton: {
    fontSize: { xs: '12px', sm: '16px' },
    fontFamily: 'Poppins',
    fontWeight: '400',
    textTransform: 'none',
    border: 'none !important',
    margin: '10px 5px 0px -5px',
    padding: '0px',
    whiteSpace: 'nowrap',
  },
  detailsgrid: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '15px',
    cursor: 'pointer',
  },
  detailstext: {
    ...poppins500,
    fontSize: { xs: '10px', sm: '14px' },
    color: '#666',
    fontWeight: 400,
    marginTop: '10px',
  },
};
export default SolicitorPopupHandler;
